import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

interface RequestHistoryTableProps {
  requests: any[];
  loading: boolean;
  onDelete: (ids: string[]) => Promise<void>;
}

export const RequestHistoryTable: React.FC<RequestHistoryTableProps> = ({
  requests,
  loading,
  onDelete,
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(requests.map(r => r.job_id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id: string) => {
    const newSelected = selected.includes(id)
      ? selected.filter(s => s !== id)
      : [...selected, id];
    setSelected(newSelected);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    setError(null);
    try {
      await onDelete(selected);
      setSelected([]);
      setConfirmOpen(false);
    } catch (error: any) {
      console.error('Delete error:', error);
      setError(error.message || 'Failed to delete requests');
      setConfirmOpen(false);
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {selected.length > 0 && (
        <Button
          variant="contained"
          color="error"
          onClick={() => setConfirmOpen(true)}
          sx={{ mb: 2 }}
          disabled={deleteLoading}
        >
          {deleteLoading ? 'Deleting...' : 'Delete Selected'}
        </Button>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < requests.length}
                  checked={selected.length === requests.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Job ID</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Document Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submitted</TableCell>
              <TableCell>Results</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request) => (
              <TableRow
                key={request.job_id}
                selected={selected.includes(request.job_id)}
                hover
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.includes(request.job_id)}
                    onChange={() => handleSelect(request.job_id)}
                  />
                </TableCell>
                <TableCell>{request.job_id}</TableCell>
                <TableCell>{request.user_name}</TableCell>
                <TableCell>{request.document_type}</TableCell>
                <TableCell>{request.status}</TableCell>
                <TableCell>{request.created_at}</TableCell>
                <TableCell>{request.ai_result?.result ? JSON.stringify(request.ai_result.result) : request.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog 
        open={confirmOpen} 
        onClose={() => !deleteLoading && setConfirmOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selected.length} selected requests?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmOpen(false)}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RequestHistoryTable;
